/**
 * TCPN PHASE S1-C1 — TreatX Admin: clinic-logo moderation queue.
 *
 * Pending logos surface here. Admin can preview and approve, or reject
 * with a comment. Approving a logo demotes any prior approved logo for
 * the same clinic to `superseded` and unsets `is_published`. History is
 * preserved server-side; rejected logos remain visible to the clinic
 * admin only.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import api from '../../utils/api';
import { CheckCircle2, X, RefreshCw, ImageIcon } from 'lucide-react';
// MEDIA-001 (2026-02-28): replaced the local AuthLogoThumb (axios
// responseType:'blob', no AbortController, no fallback chain) with the
// canonical AuthMediaThumb. Same auth path, same RBAC, same visibility —
// hardened against the CRA blob/XHR overlay race.
import AuthMediaThumb from '../../components/AuthMediaThumb';

export default function LogoModerationQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectComment, setRejectComment] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/logos/pending');
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error('Failed to load moderation queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const handleApprove = async (mediaId) => {
    if (!window.confirm('Approve this logo? It becomes the canonical brand mark for this clinic.')) return;
    try {
      await api.post(`/admin/logos/${mediaId}/approve`);
      toast.success('Logo approved');
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Approval failed';
      toast.error(msg);
    }
  };

  const handleReject = async (mediaId) => {
    const comment = rejectComment.trim();
    if (comment.length < 4) {
      toast.error('Please enter a rejection reason (min 4 characters)');
      return;
    }
    try {
      await api.post(`/admin/logos/${mediaId}/reject`, { comment });
      toast.success('Logo rejected');
      setRejectingId(null);
      setRejectComment('');
      refresh();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Rejection failed';
      toast.error(msg);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="logo-moderation-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Logo moderation</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pending clinic-logo submissions. Approve to set as the canonical
            brand mark; reject with a comment so the clinic can correct and
            resubmit.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading} data-testid="logo-mod-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="logo-mod-loading">Loading…</div>
      ) : items.length === 0 ? (
        <Card data-testid="logo-mod-empty">
          <CardContent className="py-12 text-center text-slate-500">
            <ImageIcon className="mx-auto h-12 w-12 text-slate-300 mb-3" />
            <p className="font-medium">No logos awaiting review.</p>
            <p className="text-xs mt-1">New submissions appear here in real time.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {items.map((it) => (
            <Card key={it.media_id} data-testid={`logo-mod-row-${it.media_id}`}>
              <CardContent className="p-4">
                <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_auto] gap-4 items-center">
                  <div className="aspect-square bg-slate-50 border rounded overflow-hidden">
                    <AuthMediaThumb
                      mediaId={it.media_id}
                      alt={`logo ${it.clinic_name}`}
                      variants={['preview', 'thumbnail', 'original']}
                      className="w-full h-full object-contain"
                      testIdPrefix="logo-mod-thumb"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{it.clinic_name}</Badge>
                      <span className="text-xs text-slate-500">{it.owner_id}</span>
                    </div>
                    <div className="text-xs text-slate-600">
                      {it.width}×{it.height}px · {(it.size_bytes / 1024).toFixed(0)} KB · {it.mime}
                    </div>
                    <div className="text-xs text-slate-500">
                      Uploaded: {formatDateTimeLocale(it.uploaded_at)}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleApprove(it.media_id)}
                      data-testid={`logo-approve-${it.media_id}`}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setRejectingId(it.media_id); setRejectComment(''); }}
                      data-testid={`logo-reject-${it.media_id}`}
                    >
                      <X className="h-4 w-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
                {rejectingId === it.media_id && (
                  <div className="mt-3 pt-3 border-t space-y-2" data-testid={`logo-reject-form-${it.media_id}`}>
                    <Textarea
                      value={rejectComment}
                      onChange={(e) => setRejectComment(e.target.value)}
                      placeholder="Reason for rejection (visible to the clinic admin)"
                      maxLength={500}
                      data-testid={`logo-reject-comment-${it.media_id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>Cancel</Button>
                      <Button size="sm" onClick={() => handleReject(it.media_id)} data-testid={`logo-reject-submit-${it.media_id}`}>
                        Submit rejection
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
